import { useEffect, useState } from "react";
import { AdminSidebar } from "@/components/Sidebar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Switch } from "@/components/ui/switch";
import { Avatar } from "@/components/Avatar";
import { toast } from "sonner";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Users, Wallet, TrendingUp, AlertCircle, Ban, CheckCircle2, Trash2, KeyRound, Pencil, Timer, PlayCircle, Crown, CrownIcon, CreditCard, Lock, Unlock, Send, Plus, Minus } from "lucide-react";import api, { formatError } from "@/lib/api";
import { formatMoney } from "@/lib/utils";
import { useTheme } from "@/context/ThemeContext";

const isVideoUrl = (u) => /\.(mp4|mov|webm)(\?|$)/i.test(u || "");

const Badge = ({ s }) => {
  const map = { PAID: "bg-green-500/20 text-green-400", PENDING: "bg-yellow-500/20 text-yellow-400", FAILED: "bg-red-500/20 text-red-400", REFUNDED: "bg-gray-500/20 text-gray-300", CANCELLED: "bg-gray-500/20 text-gray-400", ACTIVE: "bg-green-500/20 text-green-400", BLOCKED: "bg-red-500/20 text-red-400", DELETED: "bg-gray-500/20 text-gray-400" };
  return <span className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold ${map[s] || "bg-white/10"}`}>{s}</span>;
};

const Shell = ({ title, children, actions }) => (
  <div className="stream-dark flex flex-col lg:flex-row min-h-screen"><AdminSidebar />
    <main className="flex-1 p-4 sm:p-6 lg:p-10 stream-scroll">
      <div className="flex items-center justify-between mb-6 sm:mb-8 flex-wrap gap-3">
        <h1 className="font-heading font-black text-2xl sm:text-3xl lg:text-4xl text-white">{title}</h1>
        {actions}
      </div>
      {children}
    </main>
  </div>
);

const Card = ({ label, value, icon: Icon, accent = "text-white" }) => (
  <div className="card-dark p-4 sm:p-6" data-testid={`admin-stat-${label}`}>
    <div className="flex justify-between"><span className="text-[10px] sm:text-xs uppercase tracking-widest text-white/40">{label}</span><Icon size={16} className={accent}/></div>
    <div className={`mt-2 sm:mt-3 font-heading font-black text-lg sm:text-2xl ${accent}`}>{value}</div>
  </div>
);

export function AdminDashboard() {
  const [s, setS] = useState(null);
  const { theme } = useTheme();
  const isLight = theme === "light";
  useEffect(() => {
    const load = async () => { try { const { data } = await api.get("/admin/summary"); setS(data); } catch (e) { toast.error(formatError(e)); } };
    load();
    const t = setInterval(load, 20000);
    return () => clearInterval(t);
  }, []);
  if (!s) return <Shell title="Dashboard"><div className="text-white/50">Učitavanje…</div></Shell>;
  return (
    <Shell title="Dashboard">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-6 sm:mb-8">
        <Card label="Korisnici" value={s.users_total} icon={Users} />
        <Card label="Aktivni sada" value={s.users_online ?? 0} icon={CheckCircle2} accent="text-green-400"/>
        <Card label="Blokirani" value={s.users_blocked} icon={Ban} accent="text-red-400"/>
        <Card label="Uplate" value={s.payments_total} icon={Wallet} />
        <Card label="Ukupan prihod" value={`${formatMoney(s.revenue)} €`} icon={TrendingUp} accent="text-[#FFD60A]"/>
        <Card label="Danas" value={s.payments_today} icon={Wallet} />
        <Card label="Ovaj mjesec" value={s.payments_month} icon={Wallet} />
        <Card label="Pending / Failed" value={`${s.pending} / ${s.failed}`} icon={AlertCircle} accent="text-yellow-400"/>
      </div>
      <div className="card-dark p-4 sm:p-6 mb-6 sm:mb-8" data-testid="online-users-panel">
        <h3 className="font-heading font-bold text-lg sm:text-xl text-white mb-3 flex items-center gap-2">
          <span className="h-2.5 w-2.5 rounded-full bg-green-500 animate-pulse"></span> Trenutno u aplikaciji
        </h3>
        {(s.online_users || []).length === 0 ? (
          <div className="text-white/50 text-sm">Nitko nije trenutno aktivan.</div>
        ) : (
          <div className="flex flex-wrap gap-3">
            {s.online_users.map((u) => (
              <div key={u.id} className="flex items-center gap-3 px-3 py-2 rounded-xl bg-green-500/10 border border-green-500/25" data-testid={`online-user-${u.id}`}>
                <div className="relative"><Avatar user={u} size={36} /><span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full bg-green-500 border-2 border-[#171717]"></span></div>
                <div className="min-w-0">
                  <div className="text-white text-sm font-semibold truncate">{u.first_name} {u.last_name}</div>
                  <div className="text-white/40 text-xs truncate">@{u.username} · {new Date(u.last_seen).toLocaleTimeString("hr-HR")}</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="card-dark p-4 sm:p-6">
        <h3 className="font-heading font-bold text-lg sm:text-xl text-white mb-3 sm:mb-4">Prihod po danu (zadnjih 14 dana)</h3>
        <div className="h-56 sm:h-72">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={s.revenue_by_day}>
              <CartesianGrid strokeDasharray="3 3" stroke={isLight ? "rgba(0,0,0,0.08)" : "rgba(255,255,255,0.06)"}/>
              <XAxis dataKey="date" stroke={isLight ? "rgba(0,0,0,0.5)" : "rgba(255,255,255,0.4)"} fontSize={12}/>
              <YAxis stroke={isLight ? "rgba(0,0,0,0.5)" : "rgba(255,255,255,0.4)"} fontSize={12}/>
              <Tooltip contentStyle={isLight ? { background: "#fff", border: "1px solid rgba(0,0,0,0.1)", borderRadius: 8, color: "#0a0a0a" } : { background: "#171717", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, color: "#fff" }}/>
              <Line type="monotone" dataKey="amount" stroke="#FFD60A" strokeWidth={2} dot={{ fill: "#FFD60A" }}/>
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>
    </Shell>
  );
}

export function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [confirmDel, setConfirmDel] = useState(null);
  const [resetPwd, setResetPwd] = useState(null);
  const [newPwd, setNewPwd] = useState("");
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({ first_name: "", last_name: "", username: "", email: "" });
  const [walletUser, setWalletUser] = useState(null);
  const [walletAmt, setWalletAmt] = useState("");
  const [walletNote, setWalletNote] = useState("");
  const [walletMode, setWalletMode] = useState("add");

  const load = async () => { try { const { data } = await api.get("/admin/users"); setUsers(data); } catch (e) { toast.error(formatError(e)); } };
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);

  const doAction = async (u, action) => {
    try {
      if (action === "block") await api.post(`/admin/users/${u.id}/block`);
      if (action === "unblock") await api.post(`/admin/users/${u.id}/unblock`);
      if (action === "delete") await api.delete(`/admin/users/${u.id}`);
      if (action === "unpause") await api.post(`/admin/users/${u.id}/unpause`);
      if (action === "grant-pro") await api.post(`/admin/users/${u.id}/grant-pro`);
      if (action === "revoke-pro") await api.post(`/admin/users/${u.id}/revoke-pro`);
      if (action === "cards-block-all") { const { data } = await api.post(`/admin/users/${u.id}/cards/block-all`); toast.success(`Blokirano ${data.blocked} kartica`); load(); return; }
      if (action === "cards-unblock-all") { const { data } = await api.post(`/admin/users/${u.id}/cards/unblock-all`); toast.success(`Odblokirano ${data.unblocked} kartica`); load(); return; }
      toast.success("Uspješno");
      load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const doPause = async (u, hours) => {
    try { await api.post(`/admin/users/${u.id}/pause`, { hours }); toast.success(`Pauziran ${hours}h`); load(); }
    catch (e) { toast.error(formatError(e)); }
  };

  const doReset = async () => {
    try {
      await api.post(`/admin/users/${resetPwd.id}/reset-password`, { new_password: newPwd });
      toast.success("Lozinka resetirana");
      setResetPwd(null); setNewPwd("");
    } catch (e) { toast.error(formatError(e)); }
  };

  const openEdit = (u) => { setEditUser(u); setEditForm({ first_name: u.first_name || "", last_name: u.last_name || "", username: u.username || "", email: u.email || "" }); };
  const saveEdit = async () => {
    try { await api.put(`/admin/users/${editUser.id}`, editForm); toast.success("Spremljeno"); setEditUser(null); load(); }
    catch (e) { toast.error(formatError(e)); }
  };

  const openWallet = (u, mode) => { setWalletUser(u); setWalletMode(mode); setWalletAmt(""); setWalletNote(""); };
  const submitWallet = async () => {
    const val = parseFloat(walletAmt);
    if (!val || val <= 0) { toast.error("Unesite valjan iznos"); return; }
    const delta = walletMode === "add" ? val : -val;
    try {
      const { data } = await api.post(`/admin/users/${walletUser.id}/wallet-adjust`, { amount: delta, note: walletNote });
      toast.success(`Novi saldo: ${formatMoney(data.balance)} €`);
      setWalletUser(null); load();
    } catch (e) { toast.error(formatError(e)); }
  };

  const isPaused = (u) => u.paused_until && new Date(u.paused_until) > new Date();

  return (
    <Shell title="Korisnici">
      <div className="card-dark overflow-x-auto">
        <table className="w-full min-w-[1000px]">
          <thead className="text-left text-xs uppercase tracking-widest text-white/40 border-b border-white/10">
            <tr><th className="p-3"></th><th className="p-3">Ime</th><th className="p-3">Username</th><th className="p-3">Email</th><th className="p-3">Registriran</th><th className="p-3">Status</th><th className="p-3">Pro</th><th className="p-3">Uplate</th><th className="p-3">Ukupno</th><th className="p-3">Saldo</th><th className="p-3">Akcije</th></tr>
          </thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-white/5 hover:bg-white/5" data-testid={`user-row-${u.id}`}>
                <td className="p-3"><div className="relative">
                  <Avatar user={u} size={40} />
                  {u.online && <span className="absolute -bottom-0.5 -right-0.5 h-3.5 w-3.5 rounded-full bg-green-500 border-2 border-[#171717]" data-testid={`online-dot-${u.id}`}></span>}
                </div></td>
                <td className="p-3 text-white">{u.first_name} {u.last_name}</td>
                <td className="p-3 text-white/70">{u.username}</td>
                <td className="p-3 text-white/70">{u.email}</td>
                <td className="p-3 text-white/50 text-sm">{new Date(u.created_at).toLocaleDateString("hr-HR")}</td>
                <td className="p-3">
                  <div className="flex flex-col gap-1">
                    <Badge s={u.status}/>
                    {u.online && <span className="inline-flex items-center gap-1 text-xs text-green-400" data-testid={`online-label-${u.id}`}><span className="h-2 w-2 rounded-full bg-green-500 animate-pulse"></span> Aktivan sada</span>}
                    {isPaused(u) && <span className="text-xs text-yellow-400">Pauziran do {new Date(u.paused_until).toLocaleString("hr-HR")}</span>}
                  </div>
                </td>
                <td className="p-3">{u.pro_status === "ACTIVE" ? <span className="px-2 py-1 rounded-full text-xs bg-[#FFD60A]/20 text-[#FFD60A] font-semibold">PRO</span> : <span className="text-white/40 text-xs">–</span>}</td>
                <td className="p-3 text-white">{u.payment_count}</td>
                <td className="p-3 text-[#FFD60A] font-semibold">{formatMoney(u.total_paid)} €</td>
                <td className="p-3 text-green-400 font-semibold" data-testid={`user-balance-${u.id}`}>{formatMoney((u.wallet_balance ?? 0))} €</td>
                <td className="p-3 flex gap-1 flex-wrap">
                  <Button size="sm" variant="ghost" className="h-8 text-white/70 hover:bg-white/10" onClick={() => openEdit(u)} data-testid={`edit-${u.id}`}><Pencil size={14}/></Button>
                  <Button size="sm" variant="ghost" className="h-8 text-green-400 hover:bg-green-500/10" onClick={() => openWallet(u, "add")} title="Dodaj u novčanik" data-testid={`wallet-add-${u.id}`}><Plus size={14}/></Button>
                  <Button size="sm" variant="ghost" className="h-8 text-orange-400 hover:bg-orange-500/10" onClick={() => openWallet(u, "sub")} title="Oduzmi iz novčanika" data-testid={`wallet-sub-${u.id}`}><Minus size={14}/></Button>
                  {(u.cards_active > 0 || u.cards_blocked > 0) && (
                    <Button size="sm" variant="ghost"
                      className={`h-8 ${u.cards_active > 0 ? "text-yellow-400 hover:bg-yellow-500/10" : "text-green-400 hover:bg-green-500/10"}`}
                      title={u.cards_active > 0 ? `Blokiraj sve kartice (${u.cards_active})` : `Odblokiraj sve kartice (${u.cards_blocked})`}
                      onClick={() => doAction(u, u.cards_active > 0 ? "cards-block-all" : "cards-unblock-all")}
                      data-testid={`cards-toggle-${u.id}`}>
                      {u.cards_active > 0 ? <Lock size={14}/> : <Unlock size={14}/>}
                    </Button>
                  )}
                  {u.pro_status === "ACTIVE" ? (
                    <Button size="sm" variant="ghost" className="h-8 text-white/60 hover:bg-white/10" onClick={() => doAction(u, "revoke-pro")} title="Oduzmi Lifetime Pro" data-testid={`revoke-pro-${u.id}`}><Crown size={14} className="line-through opacity-60"/></Button>
                  ) : (
                    <Button size="sm" variant="ghost" className="h-8 text-[#FFD60A] hover:bg-[#FFD60A]/10" onClick={() => doAction(u, "grant-pro")} title="Dodijeli Lifetime Pro" data-testid={`grant-pro-${u.id}`}><Crown size={14}/></Button>
                  )}
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild><Button size="sm" variant="ghost" className="h-8 text-yellow-400 hover:bg-yellow-500/10" data-testid={`pause-${u.id}`}><Timer size={14}/></Button></DropdownMenuTrigger>
                    <DropdownMenuContent className="bg-[#171717] border-white/10 text-white">
                      {[24, 48, 60, 90].map((h) => <DropdownMenuItem key={h} onClick={() => doPause(u, h)} data-testid={`pause-${u.id}-${h}`}>Pauziraj {h}h</DropdownMenuItem>)}
                      {isPaused(u) && <DropdownMenuItem onClick={() => doAction(u, "unpause")} data-testid={`unpause-${u.id}`} className="text-green-400"><PlayCircle size={14} className="mr-2"/>Odpauziraj</DropdownMenuItem>}
                    </DropdownMenuContent>
                  </DropdownMenu>
                  {u.status !== "DELETED" && (
                    <Button size="sm" variant="ghost"
                      className={`h-8 ${u.status === "ACTIVE" ? "text-orange-400 hover:bg-orange-500/10" : "text-green-400 hover:bg-green-500/10"}`}
                      onClick={() => doAction(u, u.status === "ACTIVE" ? "block" : "unblock")}
                      data-testid={`status-toggle-${u.id}`}>
                      {u.status === "ACTIVE" ? <Ban size={14}/> : <CheckCircle2 size={14}/>}
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" className="h-8 text-blue-400 hover:bg-blue-500/10" onClick={() => setResetPwd(u)} data-testid={`reset-${u.id}`}><KeyRound size={14}/></Button>
                  <Button size="sm" variant="ghost" className="h-8 text-red-400 hover:bg-red-500/10" onClick={() => setConfirmDel(u)} data-testid={`del-${u.id}`}><Trash2 size={14}/></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <AlertDialogContent className="bg-[#171717] border-white/10 text-white">
          <AlertDialogHeader><AlertDialogTitle>Trajno obriši korisnika?</AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">Korisnik i sve njegove uplate se brišu iz baze. Ova akcija je nepovratna.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/5 border-white/10 text-white">Otkaži</AlertDialogCancel>
            <AlertDialogAction onClick={() => { doAction(confirmDel, "delete"); setConfirmDel(null); }} className="bg-red-500 hover:bg-red-600" data-testid="confirm-delete">Obriši</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog open={!!resetPwd} onOpenChange={(o) => !o && setResetPwd(null)}>
        <DialogContent className="bg-[#171717] border-white/10 text-white">
          <DialogHeader><DialogTitle>Reset lozinke za {resetPwd?.username}</DialogTitle></DialogHeader>
          <Input type="password" placeholder="Nova lozinka" value={newPwd} onChange={(e) => setNewPwd(e.target.value)} className="bg-white/5 border-white/10" data-testid="admin-new-pwd" />
          <DialogFooter>
            <Button className="btn-yellow" onClick={doReset} data-testid="admin-reset-confirm">Postavi</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!editUser} onOpenChange={(o) => !o && setEditUser(null)}>
        <DialogContent className="bg-[#171717] border-white/10 text-white">
          <DialogHeader><DialogTitle>Uredi korisnika</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-white/80">Ime</Label><Input value={editForm.first_name} onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })} className="bg-white/5 border-white/10 mt-2" data-testid="edit-first"/></div>
              <div><Label className="text-white/80">Prezime</Label><Input value={editForm.last_name} onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })} className="bg-white/5 border-white/10 mt-2" data-testid="edit-last"/></div>
            </div>
            <div><Label className="text-white/80">Username</Label><Input value={editForm.username} onChange={(e) => setEditForm({ ...editForm, username: e.target.value })} className="bg-white/5 border-white/10 mt-2" data-testid="edit-user"/></div>
            <div><Label className="text-white/80">Email</Label><Input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} className="bg-white/5 border-white/10 mt-2" data-testid="edit-email"/></div>
          </div>
          <DialogFooter><Button className="btn-yellow" onClick={saveEdit} data-testid="edit-save">Spremi</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!walletUser} onOpenChange={(o) => !o && setWalletUser(null)}>
        <DialogContent className="bg-[#171717] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle>{walletMode === "add" ? "Dodaj u novčanik" : "Oduzmi iz novčanika"}</DialogTitle>
          </DialogHeader>
          {walletUser && (
            <div className="space-y-3">
              <div className="text-white/70 text-sm">
                Korisnik: <span className="text-white font-semibold">{walletUser.first_name} {walletUser.last_name}</span> ({walletUser.email})
              </div>
              <div className="text-white/70 text-sm">
                Trenutni saldo: <span className="text-[#FFD60A] font-semibold">{formatMoney((walletUser.wallet_balance ?? 0))} €</span>
              </div>
              <div>
                <Label className="text-white/80">Iznos (EUR)</Label>
                <Input type="number" step="0.01" min="0.01" value={walletAmt} onChange={(e) => setWalletAmt(e.target.value)} className="bg-white/5 border-white/10 mt-2 rounded-xl" data-testid="wallet-amt"/>
              </div>
              <div>
                <Label className="text-white/80">Napomena (opcionalno)</Label>
                <Input value={walletNote} onChange={(e) => setWalletNote(e.target.value)} className="bg-white/5 border-white/10 mt-2 rounded-xl" data-testid="wallet-note"/>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button className={walletMode === "add" ? "btn-yellow" : "bg-orange-500 hover:bg-orange-600 text-white"} onClick={submitWallet} data-testid="wallet-submit">
              {walletMode === "add" ? "Dodaj" : "Oduzmi"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}

export function AdminPayments() {
  const [items, setItems] = useState([]);
  const [statusF, setStatusF] = useState("all");
  const [q, setQ] = useState("");
  const [confirmDel, setConfirmDel] = useState(null);
  const load = async () => {
    try {
      const params = {};
      if (statusF && statusF !== "all") params.status = statusF;
      if (q) params.q = q;
      const { data } = await api.get("/admin/payments", { params });
      setItems(data);
    } catch (e) { toast.error(formatError(e)); }
  };
  useEffect(() => { load(); }, [statusF]);
  const doDelete = async () => {
    try { await api.delete(`/admin/payments/${confirmDel.id}`); toast.success("Uplata obrisana"); setConfirmDel(null); load(); }
    catch (e) { toast.error(formatError(e)); }
  };
  return (
    <Shell title="Uplate">
      <div className="flex gap-3 mb-4 flex-wrap">
        <Input placeholder="Pretraga (opis / email)…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} className="bg-white/5 border-white/10 text-white max-w-sm" data-testid="pay-search"/>
        <Select value={statusF} onValueChange={setStatusF}>
          <SelectTrigger className="bg-white/5 border-white/10 text-white w-40" data-testid="pay-filter"><SelectValue placeholder="Status"/></SelectTrigger>
          <SelectContent className="bg-[#171717] border-white/10 text-white">
            <SelectItem value="all">Svi statusi</SelectItem>
            {["PAID","PENDING","FAILED","REFUNDED","CANCELLED"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
        <Button onClick={load} className="btn-yellow" data-testid="pay-apply">Primijeni</Button>
      </div>
      <div className="card-dark overflow-x-auto">
        <table className="w-full min-w-[1000px]">
          <thead className="text-left text-xs uppercase tracking-widest text-white/40 border-b border-white/10">
            <tr><th className="p-3">Korisnik</th><th className="p-3">Iznos</th><th className="p-3">Opis</th><th className="p-3">Status</th><th className="p-3">Datum</th><th className="p-3">Session ID</th><th className="p-3"></th></tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} className="border-b border-white/5 hover:bg-white/5">
                <td className="p-3 text-white text-sm">{p.user?.email}</td>
                <td className="p-3 text-white font-semibold">{formatMoney(p.amount)} {p.currency}</td>
                <td className="p-3 text-white/60">{p.description}</td>
                <td className="p-3"><Badge s={p.status}/></td>
                <td className="p-3 text-white/50 text-sm">{new Date(p.created_at).toLocaleString("hr-HR")}</td>
                <td className="p-3 text-white/30 text-xs font-mono truncate max-w-[180px]">{p.stripe_checkout_session_id}</td>
                <td className="p-3"><Button size="sm" variant="ghost" className="h-8 text-red-400 hover:bg-red-500/10" onClick={() => setConfirmDel(p)} data-testid={`del-pay-${p.id}`}><Trash2 size={14}/></Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <AlertDialogContent className="bg-[#171717] border-white/10 text-white">
          <AlertDialogHeader><AlertDialogTitle>Obriši uplatu?</AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">Uplata se trajno briše iz baze. Stripe transakcija ostaje netaknuta.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/5 border-white/10 text-white">Otkaži</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-500 hover:bg-red-600" data-testid="confirm-del-pay">Obriši</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Shell>
  );
}

export function AdminPaymentRequests() {
  const [items, setItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ user_id: "", amount: "", currency: "EUR", description: "", due_date: "" });

  const load = async () => {
    try { const [{ data: it }, { data: us }] = await Promise.all([api.get("/admin/payment-requests"), api.get("/admin/users")]); setItems(it); setUsers(us); }
    catch (e) { toast.error(formatError(e)); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!f.user_id) { toast.error("Odaberite korisnika"); return; }
    const amt = parseFloat(f.amount);
    if (!amt || amt <= 0) { toast.error("Iznos mora biti veći od 0"); return; }
    if (!f.description?.trim()) { toast.error("Unesite opis"); return; }
    try {
      const payload = { user_id: f.user_id, amount: amt, currency: f.currency || "EUR", description: f.description };
      if (f.due_date) payload.due_date = f.due_date;
      await api.post("/admin/payment-requests", payload);
      toast.success("Zahtjev kreiran");
      setOpen(false);
      await load();
    } catch (e) { console.error("create PR", e); toast.error(formatError(e)); }
  };

  const cancel = async (id) => { try { await api.delete(`/admin/payment-requests/${id}`); toast.success("Otkazano"); load(); } catch (e) { toast.error(formatError(e)); } };

  return (
    <Shell title="Zahtjevi za uplatu" actions={<Button className="btn-yellow rounded-full" onClick={() => setOpen(true)} data-testid="pr-new">Kreiraj zahtjev</Button>}>
      <div className="card-dark overflow-x-auto">
        <table className="w-full min-w-[800px]">
          <thead className="text-left text-xs uppercase tracking-widest text-white/40 border-b border-white/10">
            <tr><th className="p-3">Korisnik</th><th className="p-3">Iznos</th><th className="p-3">Opis</th><th className="p-3">Status</th><th className="p-3">Rok</th><th className="p-3">Datum</th><th className="p-3"></th></tr>
          </thead>
          <tbody>
            {items.map((r) => (
              <tr key={r.id} className="border-b border-white/5 hover:bg-white/5">
                <td className="p-3 text-white">{r.user?.email}</td>
                <td className="p-3 text-[#FFD60A] font-semibold">{formatMoney(r.amount)} {r.currency}</td>
                <td className="p-3 text-white/60">{r.description}</td>
                <td className="p-3"><Badge s={r.status}/></td>
                <td className="p-3 text-white/50 text-sm">{r.due_date || "-"}</td>
                <td className="p-3 text-white/50 text-sm">{new Date(r.created_at).toLocaleDateString("hr-HR")}</td>
                <td className="p-3">{r.status !== "PAID" && <Button size="sm" variant="ghost" className="text-red-400 hover:bg-red-500/10" onClick={() => cancel(r.id)} data-testid={`pr-del-${r.id}`}><Trash2 size={14} className="mr-1"/>Obriši</Button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setF({ user_id: "", amount: "", currency: "EUR", description: "", due_date: "" }); }}>
        <DialogContent className="bg-[#171717] border-white/10 text-white">
          <DialogHeader><DialogTitle>Kreiraj zahtjev za uplatu</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-white/80">Korisnik</Label>
              <Select value={f.user_id} onValueChange={(v) => setF({ ...f, user_id: v })}>
                <SelectTrigger className="bg-white/5 border-white/10 text-white mt-2" data-testid="pr-user"><SelectValue placeholder="Odaberi korisnika"/></SelectTrigger>
                <SelectContent className="bg-[#171717] border-white/10 text-white max-h-72">
                  {users.map((u) => <SelectItem key={u.id} value={u.id}>{u.email} ({u.username})</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-white/80">Iznos</Label><Input type="number" step="0.01" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} className="bg-white/5 border-white/10 mt-2" data-testid="pr-amount"/></div>
              <div><Label className="text-white/80">Valuta</Label><Input value={f.currency} onChange={(e) => setF({ ...f, currency: e.target.value.toUpperCase() })} className="bg-white/5 border-white/10 mt-2"/></div>
            </div>
            <div><Label className="text-white/80">Opis</Label><Textarea value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} className="bg-white/5 border-white/10 mt-2" data-testid="pr-desc"/></div>
            <div><Label className="text-white/80">Rok (opcionalno)</Label><Input type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} className="bg-white/5 border-white/10 mt-2"/></div>
          </div>
          <DialogFooter><Button className="btn-yellow" onClick={create} data-testid="pr-create">Kreiraj</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}

export function AdminStripe() {
  const [s, setS] = useState(null);
  const [pk, setPk] = useState(""); const [sk, setSk] = useState(""); const [wh, setWh] = useState(""); const [mode, setMode] = useState("test");
  const load = async () => { try { const { data } = await api.get("/admin/stripe-settings"); setS(data); setPk(data.publishable_key || ""); setMode(data.mode || "test"); } catch (e) { toast.error(formatError(e)); } };
  useEffect(() => { load(); }, []);
  const save = async () => {
    try { await api.put("/admin/stripe-settings", { publishable_key: pk, secret_key: sk || undefined, webhook_secret: wh || undefined, mode }); toast.success("Stripe postavke spremljene"); setSk(""); setWh(""); load(); }
    catch (e) { toast.error(formatError(e)); }
  };
  const test = async () => {
    try { const { data } = await api.post("/admin/stripe-test"); toast.success(`Stripe OK — ${data.account_id}`); }
    catch (e) { toast.error(formatError(e)); }
  };
  return (
    <Shell title="Stripe postavke">
      <div className="card-dark p-8 max-w-2xl space-y-4">
        <div><Label className="text-white/80">Publishable Key</Label><Input value={pk} onChange={(e) => setPk(e.target.value)} className="bg-white/5 border-white/10 text-white mt-2 rounded-xl" data-testid="stripe-pk"/></div>
        <div><Label className="text-white/80">Secret Key {s?.secret_key_masked && <span className="text-white/40 text-xs ml-2">Trenutno: {s.secret_key_masked}</span>}</Label><Input type="password" placeholder="sk_test_… (ostavite prazno za nepromijenjeno)" value={sk} onChange={(e) => setSk(e.target.value)} className="bg-white/5 border-white/10 text-white mt-2 rounded-xl" data-testid="stripe-sk"/></div>
        <div><Label className="text-white/80">Webhook Secret {s?.webhook_secret_masked && <span className="text-white/40 text-xs ml-2">Trenutno: {s.webhook_secret_masked}</span>}</Label><Input type="password" placeholder="whsec_… (ostavite prazno za nepromijenjeno)" value={wh} onChange={(e) => setWh(e.target.value)} className="bg-white/5 border-white/10 text-white mt-2 rounded-xl" data-testid="stripe-wh"/></div>
        <div><Label className="text-white/80">Mode</Label>
          <Select value={mode} onValueChange={setMode}>
            <SelectTrigger className="bg-white/5 border-white/10 text-white mt-2" data-testid="stripe-mode"><SelectValue/></SelectTrigger>
            <SelectContent className="bg-[#171717] border-white/10 text-white">
              <SelectItem value="test">Test</SelectItem>
              <SelectItem value="live">Live</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex gap-3 pt-2">
          <Button className="btn-yellow rounded-full" onClick={save} data-testid="stripe-save">SPREMI</Button>
          <Button variant="outline" className="rounded-full bg-transparent border-white/20 text-white hover:bg-white/10" onClick={test} data-testid="stripe-test">TEST STRIPE CONNECTION</Button>
        </div>
        <p className="text-white/40 text-xs mt-4">Webhook URL: <code className="text-[#FFD60A]">{`${process.env.REACT_APP_BACKEND_URL}/api/stripe/webhook`}</code></p>
      </div>
    </Shell>
  );
}

export function AdminBranding() {
  const [b, setB] = useState({ application_name: "", slogan: "", secondary_slogan: "", contact_email: "", logo_url: "", favicon_url: "", hero_logo_url: "", footer_text: "", footer_logo_url: "", support_name: "", pay_bg_url: "", card_bg_url: "", pwa_icon_url: "", login_bg_url: "", register_bg_url: "", share_logo_url: "", support_logo_url: "", support_whatsapp: "", support_tiktok: "", tiktok_qr_url: "", support_whatsapp_enabled: true, support_tiktok_enabled: true, logo_light_url: "", pay_logo_light_url: "", hero_logo_light_url: "", footer_logo_light_url: "", login_bg_light_url: "", register_bg_light_url: "", pay_bg_light_url: "", privacy_policy: "" });
  const load = async () => { try { const { data } = await api.get("/branding"); setB({ ...b, ...data }); } catch (e) { toast.error(formatError(e)); } };
  useEffect(() => { load(); }, []);
  const save = async () => { try { await api.put("/admin/branding", b); toast.success("Branding spremljen"); load(); } catch (e) { toast.error(formatError(e)); } };
  const upload = async (e, field) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file); fd.append("field", field);
    try { const { data } = await api.post("/admin/branding/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Uploadano"); setB({ ...b, [field]: data.url }); load(); }
    catch (err) { toast.error(formatError(err)); }
  };
  return (
    <Shell title="Branding">
      <div className="card-dark p-8 max-w-2xl space-y-4">
        <div><Label className="text-white/80">Naziv aplikacije</Label><Input value={b.application_name} onChange={(e) => setB({ ...b, application_name: e.target.value })} className="bg-white/5 border-white/10 text-white mt-2 rounded-xl" data-testid="brand-name"/></div>
        <div><Label className="text-white/80">Slogan</Label><Input value={b.slogan} onChange={(e) => setB({ ...b, slogan: e.target.value })} className="bg-white/5 border-white/10 text-white mt-2 rounded-xl" data-testid="brand-slogan"/></div>
        <div><Label className="text-white/80">Dodatni slogan</Label><Input value={b.secondary_slogan || ""} onChange={(e) => setB({ ...b, secondary_slogan: e.target.value })} className="bg-white/5 border-white/10 text-white mt-2 rounded-xl"/></div>
        <div><Label className="text-white/80">Kontakt email</Label><Input value={b.contact_email || ""} onChange={(e) => setB({ ...b, contact_email: e.target.value })} className="bg-white/5 border-white/10 text-white mt-2 rounded-xl"/></div>
        <div><Label className="text-white/80">Ime podrške u chatu (npr. "SMS STREAM")</Label><Input value={b.support_name || ""} onChange={(e) => setB({ ...b, support_name: e.target.value })} className="bg-white/5 border-white/10 text-white mt-2 rounded-xl" data-testid="brand-support-name"/></div>
        <div>
          <Label className="text-white/80">Logo</Label>
          <div className="flex items-center gap-4 mt-2">
            {b.logo_url && <img src={b.logo_url.startsWith("http") ? b.logo_url : `${process.env.REACT_APP_BACKEND_URL}${b.logo_url}`} alt="logo" className="asset-dark-preview h-12 bg-white/10 rounded-lg p-2"/>}
            <input type="file" accept="image/*" onChange={(e) => upload(e, "logo_url")} className="text-white/70 text-sm" data-testid="upload-logo"/>
          </div>
        </div>
        <div>
          <Label className="text-white/80">Favicon</Label>
          <div className="flex items-center gap-4 mt-2">
            {b.favicon_url && <img src={b.favicon_url.startsWith("http") ? b.favicon_url : `${process.env.REACT_APP_BACKEND_URL}${b.favicon_url}`} alt="fav" className="h-8 w-8 bg-white/10 rounded p-1"/>}
            <input type="file" accept="image/*" onChange={(e) => upload(e, "favicon_url")} className="text-white/70 text-sm" data-testid="upload-fav"/>
          </div>
        </div>
        <div>
          <Label className="text-white/80">Logo za skidanje aplikacije (PWA / Home Screen)</Label>
          <div className="flex items-center gap-4 mt-2">
            {b.pwa_icon_url && <img src={b.pwa_icon_url.startsWith("http") ? b.pwa_icon_url : `${process.env.REACT_APP_BACKEND_URL}${b.pwa_icon_url}`} alt="pwa" className="asset-dark-preview h-16 w-16 bg-white/10 rounded-2xl p-1 object-contain"/>}
            <input type="file" accept="image/png,image/*" onChange={(e) => upload(e, "pwa_icon_url")} className="text-white/70 text-sm" data-testid="upload-pwa-icon"/>
            {b.pwa_icon_url && <Button size="sm" variant="ghost" className="text-red-400 hover:bg-red-500/10" onClick={() => { setB({ ...b, pwa_icon_url: "" }); api.put("/admin/branding", { pwa_icon_url: "" }).then(() => toast.success("Slika uklonjena")); }} data-testid="clear-pwa-icon">Ukloni</Button>}
          </div>
          <p className="text-white/40 text-xs mt-2">Ova ikona se prikazuje kad korisnici dodaju aplikaciju na početni ekran mobitela ili instaliraju kao desktop PWA. Preporučeno: kvadratna, prozirna PNG, najmanje 512×512.</p>
        </div>
        <div>
          <Label className="text-white/80">Veliki logo na naslovnici (Hero) — NOĆ</Label>
          <div className="flex items-center gap-4 mt-2">
            {b.hero_logo_url && <img src={b.hero_logo_url.startsWith("http") ? b.hero_logo_url : `${process.env.REACT_APP_BACKEND_URL}${b.hero_logo_url}`} alt="hero" className="asset-dark-preview h-16 w-16 bg-white/10 rounded-full p-1 object-contain"/>}
            <input type="file" accept="image/*" onChange={(e) => upload(e, "hero_logo_url")} className="text-white/70 text-sm" data-testid="upload-hero"/>
          </div>
        </div>
        <div>
          <Label className="text-white/80">Veliki logo na naslovnici (Hero) — DAN</Label>
          <div className="flex items-center gap-4 mt-2">
            {b.hero_logo_light_url && <img src={b.hero_logo_light_url.startsWith("http") ? b.hero_logo_light_url : `${process.env.REACT_APP_BACKEND_URL}${b.hero_logo_light_url}`} alt="hero-light" className="h-16 w-16 bg-white rounded-full p-1 object-contain"/>}
            <input type="file" accept="image/*" onChange={(e) => upload(e, "hero_logo_light_url")} className="text-white/70 text-sm" data-testid="upload-hero-light"/>
            {b.hero_logo_light_url && <Button size="sm" variant="ghost" className="text-red-400 hover:bg-red-500/10" onClick={() => { setB({ ...b, hero_logo_light_url: "" }); api.put("/admin/branding", { hero_logo_light_url: "" }).then(() => toast.success("Uklonjeno")); }} data-testid="clear-hero-light">Ukloni</Button>}
          </div>
          <p className="text-white/40 text-xs mt-2">Ako je prazno, u dnevnoj temi koristi se noćni hero logo.</p>
        </div>
        <div>
          <Label className="text-white/80">Pozadinska slika payment linka — NOĆ</Label>
          <div className="flex items-center gap-4 mt-2">
            {b.pay_bg_url && <img src={b.pay_bg_url.startsWith("http") ? b.pay_bg_url : `${process.env.REACT_APP_BACKEND_URL}${b.pay_bg_url}`} alt="pay-bg" className="h-20 w-32 bg-white/10 rounded-lg object-cover"/>}
            <input type="file" accept="image/*,image/gif,video/mp4,video/quicktime,video/webm" onChange={(e) => upload(e, "pay_bg_url")} className="text-white/70 text-sm" data-testid="upload-pay-bg"/>
            {b.pay_bg_url && <Button size="sm" variant="ghost" className="text-red-400 hover:bg-red-500/10" onClick={() => { setB({ ...b, pay_bg_url: "" }); api.put("/admin/branding", { pay_bg_url: "" }).then(() => toast.success("Pozadina uklonjena")); }} data-testid="clear-pay-bg">Ukloni</Button>}
          </div>
          <p className="text-white/40 text-xs mt-2">Slika, GIF ili MP4 video prikazan iza payment kartice na /pay/… linkovima. Video se automatski reproducira u loopu, bez zvuka.</p>
        </div>
        <div>
          <Label className="text-white/80">Pozadinska slika payment linka — DAN</Label>
          <div className="flex items-center gap-4 mt-2">
            {b.pay_bg_light_url && (isVideoUrl(b.pay_bg_light_url)
              ? <video src={b.pay_bg_light_url.startsWith("http") ? b.pay_bg_light_url : `${process.env.REACT_APP_BACKEND_URL}${b.pay_bg_light_url}`} muted loop autoPlay playsInline className="h-20 w-32 bg-white/10 rounded-lg object-cover"/>
              : <img src={b.pay_bg_light_url.startsWith("http") ? b.pay_bg_light_url : `${process.env.REACT_APP_BACKEND_URL}${b.pay_bg_light_url}`} alt="pay-bg-light" className="h-20 w-32 bg-white/10 rounded-lg object-cover"/>)}
            <input type="file" accept="image/*,image/gif,video/mp4,video/quicktime,video/webm" onChange={(e) => upload(e, "pay_bg_light_url")} className="text-white/70 text-sm" data-testid="upload-pay-bg-light"/>
            {b.pay_bg_light_url && <Button size="sm" variant="ghost" className="text-red-400 hover:bg-red-500/10" onClick={() => { setB({ ...b, pay_bg_light_url: "" }); api.put("/admin/branding", { pay_bg_light_url: "" }).then(() => toast.success("Pozadina uklonjena")); }} data-testid="clear-pay-bg-light">Ukloni</Button>}
          </div>
          <p className="text-white/40 text-xs mt-2">Prikazuje se na uplatnim linkovima kad je uključena dnevna tema. Ako je prazno, koristi se noćna pozadina.</p>
        </div>
        <div>
          <Label className="text-white/80">TikTok kod (QR slika s TikTok profila)</Label>
          <div className="flex items-center gap-4 mt-2">
            {b.tiktok_qr_url && <img src={b.tiktok_qr_url.startsWith("http") ? b.tiktok_qr_url : `${process.env.REACT_APP_BACKEND_URL}${b.tiktok_qr_url}`} alt="tiktok-qr" className="h-24 w-24 bg-white rounded-lg object-contain p-1"/>}
            <input type="file" accept="image/*" onChange={(e) => upload(e, "tiktok_qr_url")} className="text-white/70 text-sm" data-testid="upload-tiktok-qr"/>
            {b.tiktok_qr_url && <Button size="sm" variant="ghost" className="text-red-400 hover:bg-red-500/10" onClick={() => { setB({ ...b, tiktok_qr_url: "" }); api.put("/admin/branding", { tiktok_qr_url: "" }).then(() => toast.success("Kod uklonjen")); }} data-testid="clear-tiktok-qr">Ukloni</Button>}
          </div>
          <p className="text-white/40 text-xs mt-2">Kad je postavljen, klik na TikTok u meniju otvara prozor s kodom koji korisnici mogu skenirati.</p>
        </div>
        <div className="flex items-center justify-between gap-4 p-4 rounded-xl bg-white/5 border border-white/10">
          <div>
            <div className="text-white font-semibold">Prikaži WhatsApp kontakt</div>
            <p className="text-white/50 text-sm mt-1">Isključite da se WhatsApp gumb ne vidi korisnicima.</p>
          </div>
          <Switch checked={b.support_whatsapp_enabled !== false} onCheckedChange={(v) => setB({ ...b, support_whatsapp_enabled: v })} data-testid="whatsapp-enabled-toggle"/>
        </div>
        <div className="flex items-center justify-between gap-4 p-4 rounded-xl bg-white/5 border border-white/10">
          <div>
            <div className="text-white font-semibold">Prikaži TikTok kontakt</div>
            <p className="text-white/50 text-sm mt-1">Isključite da se TikTok gumb ne vidi korisnicima.</p>
          </div>
          <Switch checked={b.support_tiktok_enabled !== false} onCheckedChange={(v) => setB({ ...b, support_tiktok_enabled: v })} data-testid="tiktok-enabled-toggle"/>
        </div>
        <div>
          <Label className="text-white/80">Logo za DNEVNU (svjetlu) temu</Label>          <div className="flex items-center gap-4 mt-2">
            {b.logo_light_url && <img src={b.logo_light_url.startsWith("http") ? b.logo_light_url : `${process.env.REACT_APP_BACKEND_URL}${b.logo_light_url}`} alt="logo-light" className="h-14 w-auto bg-white rounded-lg object-contain p-1"/>}
            <input type="file" accept="image/*" onChange={(e) => upload(e, "logo_light_url")} className="text-white/70 text-sm" data-testid="upload-logo-light"/>
            {b.logo_light_url && <Button size="sm" variant="ghost" className="text-red-400 hover:bg-red-500/10" onClick={() => { setB({ ...b, logo_light_url: "" }); api.put("/admin/branding", { logo_light_url: "" }).then(() => toast.success("Logo uklonjen")); }} data-testid="clear-logo-light">Ukloni</Button>}
          </div>
          <p className="text-white/40 text-xs mt-2">Prikazuje se kad korisnik uključi dnevnu (bijelu) temu. Preporučeno: logo s tamnim tekstom.</p>
        </div>
        <div>
          <Label className="text-white/80">Logo na uplatnim linkovima (dnevna tema)</Label>
          <div className="flex items-center gap-4 mt-2">
            {b.pay_logo_light_url && <img src={b.pay_logo_light_url.startsWith("http") ? b.pay_logo_light_url : `${process.env.REACT_APP_BACKEND_URL}${b.pay_logo_light_url}`} alt="pay-logo-light" className="h-14 w-auto bg-white rounded-lg object-contain p-1"/>}
            <input type="file" accept="image/*" onChange={(e) => upload(e, "pay_logo_light_url")} className="text-white/70 text-sm" data-testid="upload-pay-logo-light"/>
            {b.pay_logo_light_url && <Button size="sm" variant="ghost" className="text-red-400 hover:bg-red-500/10" onClick={() => { setB({ ...b, pay_logo_light_url: "" }); api.put("/admin/branding", { pay_logo_light_url: "" }).then(() => toast.success("Logo uklonjen")); }} data-testid="clear-pay-logo-light">Ukloni</Button>}
          </div>
          <p className="text-white/40 text-xs mt-2">Ako je prazno, na uplatnim linkovima se u dnevnoj temi koristi glavni dnevni logo.</p>
        </div>
        <div>
          <Label className="text-white/80">Logo za kontakt podrške (WhatsApp)</Label>
          <div className="flex items-center gap-4 mt-2">
            {b.support_logo_url && <img src={b.support_logo_url.startsWith("http") ? b.support_logo_url : `${process.env.REACT_APP_BACKEND_URL}${b.support_logo_url}`} alt="support-logo" className="h-16 w-16 bg-white/10 rounded-full object-contain p-1"/>}
            <input type="file" accept="image/*" onChange={(e) => upload(e, "support_logo_url")} className="text-white/70 text-sm" data-testid="upload-support-logo"/>
            {b.support_logo_url && <Button size="sm" variant="ghost" className="text-red-400 hover:bg-red-500/10" onClick={() => { setB({ ...b, support_logo_url: "" }); api.put("/admin/branding", { support_logo_url: "" }).then(() => toast.success("Logo uklonjen")); }} data-testid="clear-support-logo">Ukloni</Button>}
          </div>
        </div>
        <div>
          <Label className="text-white/80">WhatsApp broj podrške (s pozivnim brojem, npr. 38591234567)</Label>
          <Input value={b.support_whatsapp || ""} onChange={(e) => setB({ ...b, support_whatsapp: e.target.value })} placeholder="38591234567" className="bg-white/5 border-white/10 mt-2" data-testid="support-whatsapp-input"/>
          <p className="text-white/40 text-xs mt-2">Prikazuje se kao "Kontakt WhatsApp" ispod Odjave u korisničkom meniju. Ostavite prazno da se sakrije.</p>
        </div>
        <div>
          <Label className="text-white/80">TikTok korisničko ime (npr. @stream_solutions2026)</Label>
          <Input value={b.support_tiktok || ""} onChange={(e) => setB({ ...b, support_tiktok: e.target.value })} placeholder="@stream_solutions2026" className="bg-white/5 border-white/10 mt-2" data-testid="support-tiktok-input"/>
          <p className="text-white/40 text-xs mt-2">Prikazuje se ispod WhatsApp kontakta u korisničkom meniju. Ostavite prazno da se sakrije.</p>
        </div>
        <div>
          <Label className="text-white/80">Logo za dijeljenje linka (WhatsApp / Viber / Facebook)</Label>
          <div className="flex items-center gap-4 mt-2">
            {b.share_logo_url && <img src={b.share_logo_url.startsWith("http") ? b.share_logo_url : `${process.env.REACT_APP_BACKEND_URL}${b.share_logo_url}`} alt="share-logo" className="h-20 w-20 bg-white/10 rounded-lg object-contain p-1"/>}
            <input type="file" accept="image/png,image/jpeg,image/*" onChange={(e) => upload(e, "share_logo_url")} className="text-white/70 text-sm" data-testid="upload-share-logo"/>
            {b.share_logo_url && <Button size="sm" variant="ghost" className="text-red-400 hover:bg-red-500/10" onClick={() => { setB({ ...b, share_logo_url: "" }); api.put("/admin/branding", { share_logo_url: "" }).then(() => toast.success("Logo uklonjen")); }} data-testid="clear-share-logo">Ukloni</Button>}
          </div>
          <p className="text-white/40 text-xs mt-2">Ova slika se prikazuje uz uplatni link kad ga korisnik podijeli na WhatsAppu, Viberu ili Facebooku. Preporučeno: kvadratna JPG/PNG, min. 512×512 (bez prozirnosti).</p>
        </div>
        <div>
          <Label className="text-white/80">Slika za virtualne kartice (npr. logo lava)</Label>
          <div className="flex items-center gap-4 mt-2">
            {b.card_bg_url && <img src={b.card_bg_url.startsWith("http") ? b.card_bg_url : `${process.env.REACT_APP_BACKEND_URL}${b.card_bg_url}`} alt="card-bg" className="h-20 w-20 bg-white/5 rounded-lg object-contain p-1"/>}
            <input type="file" accept="image/*" onChange={(e) => upload(e, "card_bg_url")} className="text-white/70 text-sm" data-testid="upload-card-bg"/>
            {b.card_bg_url && <Button size="sm" variant="ghost" className="text-red-400 hover:bg-red-500/10" onClick={() => { setB({ ...b, card_bg_url: "" }); api.put("/admin/branding", { card_bg_url: "" }).then(() => toast.success("Slika uklonjena")); }} data-testid="clear-card-bg">Ukloni</Button>}
          </div>
          <p className="text-white/40 text-xs mt-2">Prozirna PNG slika koja se prikazuje kao pozadina na prednjoj strani virtualnih kartica korisnika.</p>
        </div>
        <div>
          <Label className="text-white/80">Pozadina — Prijava (Login) — NOĆ</Label>
          <div className="flex items-center gap-4 mt-2">
            {b.login_bg_url && (isVideoUrl(b.login_bg_url)
              ? <video src={b.login_bg_url.startsWith("http") ? b.login_bg_url : `${process.env.REACT_APP_BACKEND_URL}${b.login_bg_url}`} muted loop autoPlay playsInline className="h-20 w-32 bg-white/10 rounded-lg object-cover"/>
              : <img src={b.login_bg_url.startsWith("http") ? b.login_bg_url : `${process.env.REACT_APP_BACKEND_URL}${b.login_bg_url}`} alt="login-bg" className="h-20 w-32 bg-white/10 rounded-lg object-cover"/>)}
            <input type="file" accept="image/*,image/gif,video/mp4,video/quicktime,video/webm" onChange={(e) => upload(e, "login_bg_url")} className="text-white/70 text-sm" data-testid="upload-login-bg"/>
            {b.login_bg_url && <Button size="sm" variant="ghost" className="text-red-400 hover:bg-red-500/10" onClick={() => { setB({ ...b, login_bg_url: "" }); api.put("/admin/branding", { login_bg_url: "" }).then(() => toast.success("Pozadina uklonjena")); }} data-testid="clear-login-bg">Ukloni</Button>}
          </div>
          <p className="text-white/40 text-xs mt-2">Slika, GIF ili MP4 video prikazan iza forme za prijavu. Video se automatski reproducira u loopu, bez zvuka.</p>
        </div>
        <div>
          <Label className="text-white/80">Pozadina — Prijava (Login) — DAN</Label>
          <div className="flex items-center gap-4 mt-2">
            {b.login_bg_light_url && (isVideoUrl(b.login_bg_light_url)
              ? <video src={b.login_bg_light_url.startsWith("http") ? b.login_bg_light_url : `${process.env.REACT_APP_BACKEND_URL}${b.login_bg_light_url}`} muted loop autoPlay playsInline className="h-20 w-32 bg-white/10 rounded-lg object-cover"/>
              : <img src={b.login_bg_light_url.startsWith("http") ? b.login_bg_light_url : `${process.env.REACT_APP_BACKEND_URL}${b.login_bg_light_url}`} alt="login-bg-light" className="h-20 w-32 bg-white/10 rounded-lg object-cover"/>)}
            <input type="file" accept="image/*,image/gif,video/mp4,video/quicktime,video/webm" onChange={(e) => upload(e, "login_bg_light_url")} className="text-white/70 text-sm" data-testid="upload-login-bg-light"/>
            {b.login_bg_light_url && <Button size="sm" variant="ghost" className="text-red-400 hover:bg-red-500/10" onClick={() => { setB({ ...b, login_bg_light_url: "" }); api.put("/admin/branding", { login_bg_light_url: "" }).then(() => toast.success("Pozadina uklonjena")); }} data-testid="clear-login-bg-light">Ukloni</Button>}
          </div>
          <p className="text-white/40 text-xs mt-2">Prikazuje se kad je uključena dnevna tema. Ako je prazno, koristi se noćna pozadina.</p>
        </div>
        <div>
          <Label className="text-white/80">Pozadina — Registracija — NOĆ</Label>
          <div className="flex items-center gap-4 mt-2">
            {b.register_bg_url && (isVideoUrl(b.register_bg_url)
              ? <video src={b.register_bg_url.startsWith("http") ? b.register_bg_url : `${process.env.REACT_APP_BACKEND_URL}${b.register_bg_url}`} muted loop autoPlay playsInline className="h-20 w-32 bg-white/10 rounded-lg object-cover"/>
              : <img src={b.register_bg_url.startsWith("http") ? b.register_bg_url : `${process.env.REACT_APP_BACKEND_URL}${b.register_bg_url}`} alt="register-bg" className="h-20 w-32 bg-white/10 rounded-lg object-cover"/>)}
            <input type="file" accept="image/*,image/gif,video/mp4,video/quicktime,video/webm" onChange={(e) => upload(e, "register_bg_url")} className="text-white/70 text-sm" data-testid="upload-register-bg"/>
            {b.register_bg_url && <Button size="sm" variant="ghost" className="text-red-400 hover:bg-red-500/10" onClick={() => { setB({ ...b, register_bg_url: "" }); api.put("/admin/branding", { register_bg_url: "" }).then(() => toast.success("Pozadina uklonjena")); }} data-testid="clear-register-bg">Ukloni</Button>}
          </div>
          <p className="text-white/40 text-xs mt-2">Slika, GIF ili MP4 video prikazan iza forme za registraciju. Video se automatski reproducira u loopu, bez zvuka.</p>
        </div>
        <div>
          <Label className="text-white/80">Pozadina — Registracija — DAN</Label>
          <div className="flex items-center gap-4 mt-2">
            {b.register_bg_light_url && (isVideoUrl(b.register_bg_light_url)
              ? <video src={b.register_bg_light_url.startsWith("http") ? b.register_bg_light_url : `${process.env.REACT_APP_BACKEND_URL}${b.register_bg_light_url}`} muted loop autoPlay playsInline className="h-20 w-32 bg-white/10 rounded-lg object-cover"/>
              : <img src={b.register_bg_light_url.startsWith("http") ? b.register_bg_light_url : `${process.env.REACT_APP_BACKEND_URL}${b.register_bg_light_url}`} alt="register-bg-light" className="h-20 w-32 bg-white/10 rounded-lg object-cover"/>)}
            <input type="file" accept="image/*,image/gif,video/mp4,video/quicktime,video/webm" onChange={(e) => upload(e, "register_bg_light_url")} className="text-white/70 text-sm" data-testid="upload-register-bg-light"/>
            {b.register_bg_light_url && <Button size="sm" variant="ghost" className="text-red-400 hover:bg-red-500/10" onClick={() => { setB({ ...b, register_bg_light_url: "" }); api.put("/admin/branding", { register_bg_light_url: "" }).then(() => toast.success("Pozadina uklonjena")); }} data-testid="clear-register-bg-light">Ukloni</Button>}
          </div>
          <p className="text-white/40 text-xs mt-2">Prikazuje se kad je uključena dnevna tema. Ako je prazno, koristi se noćna pozadina.</p>
        </div>
        <div className="pt-4 border-t border-white/10">
          <h3 className="font-heading font-bold text-lg text-white mb-2">Footer</h3>
          <Label className="text-white/80">Tekst footera (ako je unesen, zamjenjuje default © liniju)</Label>
          <Textarea value={b.footer_text || ""} onChange={(e) => setB({ ...b, footer_text: e.target.value })} rows={3} placeholder="npr. © 2026 STREAM&#10;Kontakt: info@stream.hr" className="bg-white/5 border-white/10 text-white mt-2 rounded-xl" data-testid="brand-footer-text"/>
          <p className="text-white/40 text-xs mt-2">Ostavite prazno da se prikazuje automatski "© godina naziv. slogan".</p>
        </div>
        <div>
          <Label className="text-white/80">Footer logo (ispod teksta) — NOĆ</Label>
          <div className="flex items-center gap-4 mt-2">
            {b.footer_logo_url && <img src={b.footer_logo_url.startsWith("http") ? b.footer_logo_url : `${process.env.REACT_APP_BACKEND_URL}${b.footer_logo_url}`} alt="footer" className="asset-dark-preview h-16 bg-white/10 rounded-lg p-2 object-contain"/>}
            <input type="file" accept="image/*" onChange={(e) => upload(e, "footer_logo_url")} className="text-white/70 text-sm" data-testid="upload-footer-logo"/>
          </div>
        </div>
        <div>
          <Label className="text-white/80">Footer logo — DAN</Label>
          <div className="flex items-center gap-4 mt-2">
            {b.footer_logo_light_url && <img src={b.footer_logo_light_url.startsWith("http") ? b.footer_logo_light_url : `${process.env.REACT_APP_BACKEND_URL}${b.footer_logo_light_url}`} alt="footer-light" className="h-16 bg-white rounded-lg p-2 object-contain"/>}
            <input type="file" accept="image/*" onChange={(e) => upload(e, "footer_logo_light_url")} className="text-white/70 text-sm" data-testid="upload-footer-logo-light"/>
            {b.footer_logo_light_url && <Button size="sm" variant="ghost" className="text-red-400 hover:bg-red-500/10" onClick={() => { setB({ ...b, footer_logo_light_url: "" }); api.put("/admin/branding", { footer_logo_light_url: "" }).then(() => toast.success("Uklonjeno")); }} data-testid="clear-footer-logo-light">Ukloni</Button>}
          </div>
          <p className="text-white/40 text-xs mt-2">Ako je prazno, u dnevnoj temi koristi se noćni footer logo.</p>
        </div>
        <div className="pt-4 border-t border-white/10">
          <h3 className="font-heading font-bold text-lg text-white mb-2">Politika privatnosti</h3>
          <Label className="text-white/80">Tekst politike privatnosti (prikazuje se na stranici /privacy)</Label>
          <Textarea value={b.privacy_policy || ""} onChange={(e) => setB({ ...b, privacy_policy: e.target.value })} rows={16} className="bg-white/5 border-white/10 text-white mt-2 rounded-xl font-mono text-xs leading-relaxed" data-testid="brand-privacy-policy"/>
          <p className="text-white/40 text-xs mt-2">Naslovi poput "1. Voditelj obrade..." automatski se podebljaju. Retke koji počinju s "- " prikazuju se kao popis. Prazan redak razdvaja odlomke.</p>
        </div>
        <Button className="btn-yellow rounded-full" onClick={save} data-testid="brand-save">Spremi</Button>
      </div>
    </Shell>
  );
}

export function AdminSettings() {
  const [s, setS] = useState({ currency: "EUR", min_payment: 1, max_payment: 100000, lifetime_price: 19.99, lifetime_currency: "EUR" });
  const load = async () => { try { const { data } = await api.get("/admin/settings"); setS(data); } catch (e) { toast.error(formatError(e)); } };
  useEffect(() => { load(); }, []);
  const save = async () => {
    try {
      await api.put("/admin/settings", {
        currency: s.currency,
        min_payment: parseFloat(s.min_payment),
        max_payment: parseFloat(s.max_payment),
        lifetime_price: parseFloat(s.lifetime_price),
        lifetime_currency: s.lifetime_currency,
        cards_enabled: !!s.cards_enabled,
      });
      toast.success("Postavke spremljene");
    } catch (e) { toast.error(formatError(e)); }
  };
  return (
    <Shell title="Postavke aplikacije">
      <div className="card-dark p-8 max-w-2xl space-y-6">
        <div>
          <h3 className="font-heading font-bold text-lg text-white mb-3">Standardne uplate</h3>
          <div className="space-y-3">
            <div><Label className="text-white/80">Valuta</Label><Input value={s.currency} onChange={(e) => setS({ ...s, currency: e.target.value.toUpperCase() })} className="bg-white/5 border-white/10 text-white mt-2 rounded-xl"/></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="text-white/80">Minimum</Label><Input type="number" step="0.01" value={s.min_payment} onChange={(e) => setS({ ...s, min_payment: e.target.value })} className="bg-white/5 border-white/10 text-white mt-2 rounded-xl"/></div>
              <div><Label className="text-white/80">Maksimum</Label><Input type="number" step="0.01" value={s.max_payment} onChange={(e) => setS({ ...s, max_payment: e.target.value })} className="bg-white/5 border-white/10 text-white mt-2 rounded-xl"/></div>
            </div>
          </div>
        </div>
        <div className="pt-4 border-t border-white/10">
          <h3 className="font-heading font-bold text-lg text-[#FFD60A] mb-3 flex items-center gap-2"><Crown size={18}/>Lifetime Pro paket</h3>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-white/80">Cijena</Label><Input type="number" step="0.01" min="0.5" value={s.lifetime_price} onChange={(e) => setS({ ...s, lifetime_price: e.target.value })} className="bg-white/5 border-white/10 text-white mt-2 rounded-xl" data-testid="lifetime-price"/></div>
            <div><Label className="text-white/80">Valuta</Label><Input value={s.lifetime_currency} onChange={(e) => setS({ ...s, lifetime_currency: e.target.value.toUpperCase() })} className="bg-white/5 border-white/10 text-white mt-2 rounded-xl" data-testid="lifetime-currency"/></div>
          </div>
          <p className="text-white/40 text-xs mt-2">Promjena cijene se odmah odražava na Postavkama korisnika.</p>
        </div>
        <div className="pt-4 border-t border-white/10">
          <h3 className="font-heading font-bold text-lg text-white mb-3 flex items-center gap-2"><CreditCard size={18}/>Virtualne kartice</h3>
          <div className="flex items-center justify-between gap-4 rounded-xl bg-white/5 border border-white/10 p-4">
            <div>
              <div className="text-white font-semibold">Virtualne kartice aktivne</div>
              <p className="text-white/50 text-sm mt-1">Uključite tek nakon što Stripe odobri vaš račun. Dok je isključeno, korisnici ne mogu kreirati kartice i vide poruku da čekaju odobrenje.</p>
            </div>
            <Switch checked={!!s.cards_enabled} onCheckedChange={(v) => setS({ ...s, cards_enabled: v })} data-testid="cards-enabled-toggle"/>
          </div>
        </div>
        <Button className="btn-yellow rounded-full" onClick={save} data-testid="settings-save">Spremi sve postavke</Button>
      </div>
    </Shell>
  );
}

export function AdminAuditLog() {
  const [items, setItems] = useState([]);
  useEffect(() => { (async () => { try { const { data } = await api.get("/admin/audit-log"); setItems(data); } catch (e) { toast.error(formatError(e)); } })(); }, []);
  return (
    <Shell title="Audit Log">
      <div className="card-dark overflow-x-auto">
        <table className="w-full min-w-[700px]">
          <thead className="text-left text-xs uppercase tracking-widest text-white/40 border-b border-white/10">
            <tr><th className="p-3">Datum</th><th className="p-3">Akcija</th><th className="p-3">Admin</th><th className="p-3">Target</th><th className="p-3">Meta</th></tr>
          </thead>
          <tbody>
            {items.map((a) => (
              <tr key={a.id} className="border-b border-white/5">
                <td className="p-3 text-white/60 text-sm">{new Date(a.created_at).toLocaleString("hr-HR")}</td>
                <td className="p-3"><span className="px-2 py-1 rounded-full text-xs bg-[#FFD60A]/10 text-[#FFD60A]">{a.action}</span></td>
                <td className="p-3 text-white/70 text-xs font-mono">{a.admin_user_id?.slice(0, 8)}</td>
                <td className="p-3 text-white/70 text-xs font-mono">{a.target_user_id?.slice(0, 8) || "-"}</td>
                <td className="p-3 text-white/40 text-xs max-w-md truncate">{JSON.stringify(a.metadata)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Shell>
  );
}


const cardStatusStyle = {
  ACTIVE: "bg-green-500/20 text-green-400",
  BLOCKED: "bg-red-500/20 text-red-400",
  CANCELLED: "bg-gray-500/20 text-gray-300",
};

export function AdminCards() {
  const [cards, setCards] = useState([]);
  const [confirmCancel, setConfirmCancel] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const load = async () => { try { const { data } = await api.get("/admin/cards"); setCards(data); } catch (e) { toast.error(formatError(e)); } };
  useEffect(() => { load(); }, []);

  const toggleBlock = async (c) => {
    try { await api.post(`/admin/cards/${c.id}/block`); toast.success(c.status === "BLOCKED" ? "Kartica odblokirana" : "Kartica blokirana"); load(); }
    catch (e) { toast.error(formatError(e)); }
  };
  const doCancel = async () => {
    try { await api.post(`/admin/cards/${confirmCancel.id}/cancel`); toast.success("Kartica stornirana"); setConfirmCancel(null); load(); }
    catch (e) { toast.error(formatError(e)); }
  };
  const doDelete = async () => {
    try { await api.delete(`/admin/cards/${confirmDel.id}`); toast.success("Kartica obrisana"); setConfirmDel(null); load(); }
    catch (e) { toast.error(formatError(e)); }
  };

  return (
    <Shell title="Virtualne kartice">
      <div className="card-dark overflow-x-auto">
        <table className="w-full min-w-[900px]">
          <thead className="text-left text-xs uppercase tracking-widest text-white/40 border-b border-white/10">
            <tr><th className="p-3">Korisnik</th><th className="p-3">Vlasnik</th><th className="p-3">Broj (maskiran)</th><th className="p-3">Istek</th><th className="p-3">Status</th><th className="p-3">Kreirano</th><th className="p-3">Akcije</th></tr>
          </thead>
          <tbody>
            {cards.length === 0 ? (
              <tr><td colSpan="7" className="p-8 text-center text-white/50">Nema kartica.</td></tr>
            ) : cards.map((c) => (
              <tr key={c.id} className="border-b border-white/5 hover:bg-white/5" data-testid={`admin-card-${c.id}`}>
                <td className="p-3 text-white/80 text-sm">{c.user?.email || "-"}</td>
                <td className="p-3 text-white text-sm">{c.holder_name}</td>
                <td className="p-3 text-white/70 font-mono text-sm">{c.masked_number}</td>
                <td className="p-3 text-white/60 font-mono text-sm">{c.expiry}</td>
                <td className="p-3"><span className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold ${cardStatusStyle[c.status] || "bg-white/10"}`}>{c.status}</span></td>
                <td className="p-3 text-white/50 text-sm">{new Date(c.created_at).toLocaleString("hr-HR")}</td>
                <td className="p-3 flex gap-1 flex-wrap">
                  {c.status !== "CANCELLED" && (
                    <Button size="sm" variant="ghost" className={`h-8 ${c.status === "BLOCKED" ? "text-green-400 hover:bg-green-500/10" : "text-yellow-400 hover:bg-yellow-500/10"}`} onClick={() => toggleBlock(c)} data-testid={`card-block-${c.id}`}>
                      {c.status === "BLOCKED" ? <><Unlock size={14} className="mr-1"/>Odblokiraj</> : <><Lock size={14} className="mr-1"/>Blokiraj</>}
                    </Button>
                  )}
                  {c.status !== "CANCELLED" && (
                    <Button size="sm" variant="ghost" className="h-8 text-orange-400 hover:bg-orange-500/10" onClick={() => setConfirmCancel(c)} data-testid={`card-cancel-${c.id}`}>
                      <Ban size={14} className="mr-1"/>Storniraj
                    </Button>
                  )}
                  <Button size="sm" variant="ghost" className="h-8 text-red-400 hover:bg-red-500/10" onClick={() => setConfirmDel(c)} data-testid={`card-del-${c.id}`}>
                    <Trash2 size={14}/>
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <AlertDialog open={!!confirmCancel} onOpenChange={(o) => !o && setConfirmCancel(null)}>
        <AlertDialogContent className="bg-[#171717] border-white/10 text-white">
          <AlertDialogHeader><AlertDialogTitle>Stornirati karticu?</AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">Kartica će biti trajno onemogućena. Ova akcija se ne može poništiti.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/5 border-white/10 text-white">Otkaži</AlertDialogCancel>
            <AlertDialogAction onClick={doCancel} className="bg-orange-500 hover:bg-orange-600" data-testid="confirm-card-cancel">Storniraj</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <AlertDialogContent className="bg-[#171717] border-white/10 text-white">
          <AlertDialogHeader><AlertDialogTitle>Trajno obrisati karticu?</AlertDialogTitle>
            <AlertDialogDescription className="text-white/60">Kartica se u potpunosti briše iz baze.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/5 border-white/10 text-white">Otkaži</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} className="bg-red-500 hover:bg-red-600" data-testid="confirm-card-del">Obriši</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Shell>
  );
}

const payoutStyle = {
  PENDING: "bg-yellow-500/20 text-yellow-300",
  APPROVED: "bg-green-500/20 text-green-400",
  REJECTED: "bg-red-500/20 text-red-400",
};

export function AdminPayouts() {
  const [items, setItems] = useState([]);
  const load = async () => { try { const { data } = await api.get("/admin/payouts"); setItems(data); } catch (e) { toast.error(formatError(e)); } };
  useEffect(() => { load(); }, []);
  const approve = async (id) => {
    if (!window.confirm("Odobriti isplatu? Saldo korisnika će se odmah smanjiti.")) return;
    try { await api.post(`/admin/payouts/${id}/approve`); toast.success("Odobreno"); load(); } catch (e) { toast.error(formatError(e)); }
  };
  const reject = async (id) => {
    if (!window.confirm("Odbiti zahtjev za isplatu?")) return;
    try { await api.post(`/admin/payouts/${id}/reject`); toast.success("Odbijeno"); load(); } catch (e) { toast.error(formatError(e)); }
  };
  const del = async (id) => {
    if (!window.confirm("Trajno obrisati ovaj zahtjev za isplatu?")) return;
    try { await api.delete(`/admin/payouts/${id}`); toast.success("Obrisano"); load(); } catch (e) { toast.error(formatError(e)); }
  };

  return (
    <Shell title="Zahtjevi za isplatu">
      <div className="card-dark overflow-x-auto">
        <table className="w-full min-w-[900px]">
          <thead className="text-left text-xs uppercase tracking-widest text-white/40 border-b border-white/10">
            <tr><th className="p-3">Korisnik</th><th className="p-3">Iznos</th><th className="p-3">Saldo korisnika</th><th className="p-3">Napomena</th><th className="p-3">Status</th><th className="p-3">Kreirano</th><th className="p-3">Akcije</th></tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan="7" className="p-8 text-center text-white/50">Nema zahtjeva.</td></tr>
            ) : items.map((p) => (
              <tr key={p.id} className="border-b border-white/5 hover:bg-white/5" data-testid={`payout-${p.id}`}>
                <td className="p-3 text-white text-sm">{p.user?.email || "-"}</td>
                <td className="p-3 text-[#FFD60A] font-semibold">{formatMoney(p.amount)} {p.currency}</td>
                <td className="p-3 text-green-400">{formatMoney((p.user?.wallet_balance ?? 0))} €</td>
                <td className="p-3 text-white/60 max-w-xs truncate">{p.note || "-"}</td>
                <td className="p-3"><span className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold ${payoutStyle[p.status] || "bg-white/10"}`}>{p.status}</span></td>
                <td className="p-3 text-white/50 text-sm">{new Date(p.created_at).toLocaleString("hr-HR")}</td>
                <td className="p-3 flex gap-1">
                  {p.status === "PENDING" && (
                    <>
                      <Button size="sm" className="h-8 bg-green-500 hover:bg-green-600 text-black font-semibold" onClick={() => approve(p.id)} data-testid={`approve-${p.id}`}>
                        <CheckCircle2 size={14} className="mr-1"/>Odobri
                      </Button>
                      <Button size="sm" variant="ghost" className="h-8 text-red-400 hover:bg-red-500/10" onClick={() => reject(p.id)} data-testid={`reject-${p.id}`}>
                        <Ban size={14} className="mr-1"/>Odbij
                      </Button>
                    </>
                  )}
                  <Button size="sm" variant="ghost" className="h-8 text-red-400 hover:bg-red-500/10" onClick={() => del(p.id)} title="Trajno obriši" data-testid={`del-payout-${p.id}`}>
                    <Trash2 size={14}/>
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Shell>
  );
}
